import { Injectable } from '@angular/core';
import {
  CapacitorSQLite,
  SQLiteConnection,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';
import { SyncClockService } from './sync-clock.service';

const DB_NAME = 'guardtracker';
const DB_VERSION = 1;

// append to the list; dont make any changes to existing objects once deployed
const UPGRADE_STATEMENTS = [
  {
    toVersion: 1,
    statements: [
      `CREATE TABLE IF NOT EXISTS cached_items (
        id TEXT NOT NULL,
        type TEXT NOT NULL,
        data TEXT NOT NULL,
        subscriber_user_id TEXT NOT NULL,
        createdAt INTEGER,
        updatedAt INTEGER,
        PRIMARY KEY (id, type, subscriber_user_id)
      );`,
      `CREATE INDEX IF NOT EXISTS idx_cached_items_type ON cached_items(type);`,

      `CREATE TABLE IF NOT EXISTS saved_updates (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        eventId TEXT NOT NULL,
        type TEXT NOT NULL,
        data TEXT NOT NULL,
        subscriber_user_id TEXT NOT NULL,
        status INTEGER NOT NULL DEFAULT 1,
        retryResponse TEXT,
        createdAt INTEGER,
        updatedAt INTEGER
      );`,
      `CREATE INDEX IF NOT EXISTS idx_saved_updates_job ON saved_updates(eventId);`,
      `CREATE INDEX IF NOT EXISTS idx_saved_updates_status ON saved_updates(status);`,
      `CREATE INDEX IF NOT EXISTS idx_saved_updates_subscriber_user_id ON saved_updates(subscriber_user_id);`,
    ],
  },
  // Example for a future migration - always append, never edit the above:
  // {
  //   toVersion: 2,
  //   statements: [`ALTER TABLE saved_updates ADD COLUMN priority INTEGER DEFAULT 0;`],
  // },
];

@Injectable({
  providedIn: 'root',
})
export class SqliteDbService {
  private sqlite = new SQLiteConnection(CapacitorSQLite);
  private db: any;
  private ready!: Promise<void>;
  private resolveReady!: () => void;

  private subscriberUserId: string = '';

  constructor(private clockService: SyncClockService) {
    this.ready = new Promise((resolve) => (this.resolveReady = resolve));
    this.init();
  }

  whenReady() {
    return this.ready;
  }
  sleep(ms = 1000) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async init() {
    if (!Capacitor.isNativePlatform()) {
      return; // Stop execution here to avoid the jeep-sqlite DOM error
    }

    try {
      await this.sleep();
      this.sqlite = new SQLiteConnection(CapacitorSQLite);
      await this.sqlite.addUpgradeStatement(DB_NAME, UPGRADE_STATEMENTS);
      const consistency = await this.sqlite.checkConnectionsConsistency();
      const isConn = (await this.sqlite.isConnection(DB_NAME, false)).result;

      this.db =
        consistency.result && isConn
          ? await this.sqlite.retrieveConnection(DB_NAME, false)
          : await this.sqlite.createConnection(
              DB_NAME,
              false,
              'no_encryption',
              DB_VERSION,
              false,
            );

      await this.db.open();
      this.resolveReady();
    } catch (err) {
      console.error('DatabaseService init failed', err);
      throw err;
    }
  }

  async execute(statement: string, values: any) {
    await this.whenReady();
    await this.db.run(statement, values);
  }

  async query(statement: string, values: any) {
    await this.whenReady();
    const result = await this.db.query(statement, values);
    return result?.values;
  }

  async transaction(fn: (db: SQLiteDBConnection) => Promise<void>) {
    await this.whenReady();
    try {
      await this.db.beginTransaction();
      await fn(this.db);
      await this.db.commitTransaction();
    } catch (err) {
      await this.db.rollbackTransaction();
      throw err;
    }
  }

  async saveCachedItem(type: string, id: string, details: any) {
    await this.upsert(type, id, details);
  }

  async getCachedItemRow(type: string, id: string) {
    const row = await this.getRowFromCachedItem(type, id);
    return row ? (JSON.parse(row?.data) as any) : null;
  }
  async deleteCachedItemRow(type: string, id: string) {
    await this.removeCachedItemRow(type, id);

    return;
  }

  private async removeCachedItemRow(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    await this.execute(
      `DELETE FROM cached_items WHERE type = ? AND id = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );
  }

  private async getRowFromCachedItem(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    const rows = await this.query(
      `SELECT * FROM cached_items WHERE type = ? AND id = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );
    return rows[0] ?? null;
  }

  private async upsert(type: string, id: string, value: unknown) {
    const now = this.clockService.nowMs();
    const data = JSON.stringify(value);
    const subscriberUserId = this.getCurrentSubscriberUserId();

    await this.execute(
      `INSERT INTO cached_items (id, type, data, subscriber_user_id, createdAt, updatedAt)
       VALUES (?, ?, ?, ?, ?, ?)
       ON CONFLICT(id, type, subscriber_user_id) DO UPDATE SET data = excluded.data, updatedAt = excluded.updatedAt`,
      [id, type, data, subscriberUserId, now, now],
    );
  }

  async insertSavedUpdate(type: string, id: string, items: any) {
    await this.addSavedUpdate(type, id, items);
  }
  async getSavedUpdateRow(type: string, eventId: string) {
    return await this.getRowFromSavedUpdate(type, eventId);
  }

  async deleteSavedUpdateRow(type: string, id: string) {
    await this.removeSavedItemRow(type, id);
    return;
  }

  async getAllSavedUpdatesByStatuses(statuses: Array<number>) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    const placeholders = statuses.map(() => '?').join(', ');
    const rows = await this.query(
      `SELECT * FROM saved_updates WHERE status IN (${placeholders}) AND subscriber_user_id = ?`,
      [...statuses, subscriberUserId],
    );

    return rows || [];
  }

  private async removeSavedItemRow(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    await this.execute(
      `DELETE FROM saved_updates WHERE type = ? AND id = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );
  }
  private async getRowFromSavedUpdate(type: string, id: string) {
    const subscriberUserId = this.getCurrentSubscriberUserId();
    const rows = await this.query(
      `SELECT * FROM saved_updates WHERE type = ? AND eventId = ? AND subscriber_user_id = ?`,
      [type, id, subscriberUserId],
    );

    return rows || [];
  }

  private async addSavedUpdate(type: string, id: string, value: unknown) {
    const now = this.clockService.nowMs();
    const data = JSON.stringify(value);

    const subscriberUserId = this.getCurrentSubscriberUserId();

    await this.execute(
      `INSERT INTO saved_updates (eventId, type, data, status, subscriber_user_id, retryResponse,  createdAt, updatedAt)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, type, data, 1, subscriberUserId, '', now, now],
    );
  }
  private getCurrentSubscriberUserId() {
    if (!this.subscriberUserId) {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');

      this.subscriberUserId = userData?.subscriber?.subscriber_user_id;
    }
    return this.subscriberUserId;
  }
  resetCurrentSubscriberUserId() {
    this.subscriberUserId = '';
    this.getCurrentSubscriberUserId();
  }
}
